import {
  ApolloError,
  FetchResult,
  MutationFunctionOptions,
  useQuery,
} from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

import {
  OptOutListsQuery,
  OptOutListsQueryVariables,
  OptOutsInListQuery,
  OptOutsInListQueryVariables,
  UpdateOptOutListMutation,
} from "~frontend/__generated__/graphql";
import {
  GET_GLOBAL_OPT_OUT_SUMMARY,
  GET_OPT_OUT_LISTS,
  GET_SUBACCOUNT_OPT_OUT_SUMMARY,
  OPT_OUTS_IN_LIST,
  UPDATE_OPT_OUT_LIST,
} from "~frontend/graphql/optOuts";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";
import usePrevious from "~frontend/util/usePrevious";

import { List } from "./OptOutListTable";

export const OPT_OUT_PAGE_SIZE = 500;

const query = GET_OPT_OUT_LISTS;

export const useOptOutList = (
  listId: string | null
): {
  list: List | undefined;
  optOuts: OptOutsInListQuery["optOutsInList"];
  loading: boolean;
  optOutsLoading: boolean;
  error: ApolloError | undefined;
  requestNextPageOfOptOuts: () => Promise<void>;
  updateList: (
    options: MutationFunctionOptions<UpdateOptOutListMutation>
  ) => Promise<FetchResult<UpdateOptOutListMutation>>;
} => {
  const currentSubaccount = useSubaccount();
  const [listPage, setListPage] = useState(0);
  const previousListId = usePrevious(listId);
  useEffect(() => {
    if (listId !== previousListId) {
      setListPage(0);
    }
  }, [listId, previousListId, setListPage]);

  const variables = {
    input: {
      subaccountId: currentSubaccount?.subaccount?.id,
      listIds: listId ? [listId] : [],
    },
  };

  const { data, loading, error } = useQuery<
    OptOutListsQuery,
    OptOutListsQueryVariables
  >(query, {
    variables,
    skip: !listId,
  });

  const optOutsInListVariables = {
    input: {
      subaccountId: currentSubaccount?.subaccount?.id,
      listId: listId || "", // empty string input will not be executed because of `skip`
      pageSize: OPT_OUT_PAGE_SIZE,
    },
  };

  const {
    data: optOuts,
    loading: optOutsLoading,
    fetchMore: fetchMoreOptOuts,
  } = useQuery<OptOutsInListQuery, OptOutsInListQueryVariables>(
    OPT_OUTS_IN_LIST,
    {
      variables: optOutsInListVariables,
      skip: !listId,
    }
  );

  const [updateList] = useMutation<UpdateOptOutListMutation>(
    UPDATE_OPT_OUT_LIST,
    {
      onErrorSnackbarMessage: "Error updating list",
      refetchQueries: [
        { query: OPT_OUTS_IN_LIST, variables: optOutsInListVariables },
        currentSubaccount
          ? GET_SUBACCOUNT_OPT_OUT_SUMMARY
          : GET_GLOBAL_OPT_OUT_SUMMARY,
      ],
    }
  );

  const requestNextPage = useCallback(async () => {
    if (!listId) {
      return;
    }

    setListPage(listPage => listPage + 1);
    await fetchMoreOptOuts({
      variables: {
        input: {
          listId,
          subaccountId: currentSubaccount?.subaccount?.id,
          page: listPage + 1,
          pageSize: OPT_OUT_PAGE_SIZE,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        return {
          optOutsInList: [
            ...prev.optOutsInList,
            ...fetchMoreResult.optOutsInList,
          ],
        };
      },
    });
  }, [listId, listPage, fetchMoreOptOuts, currentSubaccount?.subaccount?.id]);

  return {
    list: data?.optOutLists?.[0],
    optOuts: optOuts?.optOutsInList || [],
    loading,
    optOutsLoading,
    error,
    requestNextPageOfOptOuts: requestNextPage,
    updateList,
  };
};
